import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth.store'

export type PlanType = 'trial' | 'starter' | 'growth' | 'pro' | 'enterprise'

export interface PlanLimits {
  maxConversations: number
  maxLeads: number
  maxTeamMembers: number
  maxKnowledgeBaseEntries: number
  knowledgeBaseFileSize: number
  maxProposals: number
  features: string[]
}

export type PlanLimitsMap = Record<PlanType, PlanLimits>

const PLAN_ORDER: PlanType[] = ['trial', 'starter', 'growth', 'pro', 'enterprise']

export function usePlan() {
  const tenant = useAuthStore((state) => state.tenant)
  const plan = (tenant?.plan || 'trial') as PlanType

  // Plan feature/limit matrix — single source of truth is the API
  // (api-server/src/services/plan.service.ts, served at GET /api/v1/plans/features).
  // Cached for the whole session; no hardcoded copy lives here anymore.
  const { data: planLimits } = useQuery({
    queryKey: ['plan-features'],
    queryFn: async () => {
      const response = await api.get('/plans/features')
      return response.data.data as PlanLimitsMap
    },
    staleTime: Infinity,
    gcTime: Infinity,
  })

  const hasFeature = (feature: string): boolean => {
    return planLimits?.[plan]?.features.includes(feature) ?? false
  }

  const getFeatureMessage = (feature: string): string | null => {
    if (!planLimits || hasFeature(feature)) return null
    const currentIndex = PLAN_ORDER.indexOf(plan)
    const upgradePlan = PLAN_ORDER.slice(currentIndex + 1).find((p) =>
      planLimits[p]?.features.includes(feature)
    )
    return upgradePlan
      ? `Upgrade to ${upgradePlan.charAt(0).toUpperCase() + upgradePlan.slice(1)}`
      : null
  }

  const getPlanLimit = (key: 'maxConversations' | 'maxLeads' | 'maxTeamMembers' | 'maxKnowledgeBaseEntries'): number => {
    return planLimits?.[plan]?.[key] ?? -1
  }

  const isTrial = plan === 'trial'
  const isStarter = plan === 'starter'
  const isGrowth = plan === 'growth'
  const isPro = plan === 'pro'
  const isEnterprise = plan === 'enterprise'

  return {
    plan,
    hasFeature,
    getFeatureMessage,
    getPlanLimit,
    isTrial,
    isStarter,
    isGrowth,
    isPro,
    isEnterprise,
  }
}
